import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_BACKOFF_MS = 15000;

/**
 * One WebSocket for presence + chat. `getHello` may be async (it can wait on the
 * browser geolocation prompt) and runs on every (re)connect. Call `reconnect()`
 * after sign-in or sign-out so the server re-reads the session cookie.
 */
export function useSocket({ onMessage, getHello }) {
  const [status, setStatus] = useState('connecting');
  const [epoch, setEpoch] = useState(0);
  const wsRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const getHelloRef = useRef(getHello);

  onMessageRef.current = onMessage;
  getHelloRef.current = getHello;

  useEffect(() => {
    let disposed = false;
    let retryTimer;
    let attempt = 0;

    const connect = () => {
      if (disposed) return;
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${window.location.host}/ws`);
      wsRef.current = ws;
      setStatus('connecting');

      ws.onopen = async () => {
        attempt = 0;
        setStatus('open');
        let hello = {};
        try { hello = (await getHelloRef.current?.()) || {}; } catch { /* geo declined */ }
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'hello', ...hello }));
      };

      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        onMessageRef.current?.(msg);
      };

      ws.onerror = () => ws.close();

      ws.onclose = () => {
        if (disposed) return;
        setStatus('closed');
        retryTimer = setTimeout(connect, Math.min(MAX_BACKOFF_MS, 600 * 2 ** attempt++));
      };
    };

    connect();
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      wsRef.current?.close();
    };
  }, [epoch]);

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const reconnect = useCallback(() => setEpoch((e) => e + 1), []);

  return { status, send, reconnect };
}
